import { Alert } from '@mui/material';
import { useState } from 'react';

import { useAppMutation } from '../api/useAppMutation';
import { useT } from '../i18n/LanguageContext';

import { FormDialog } from './FormDialog';
import { generatePassword } from './generatePassword';
import { PasswordField } from './PasswordField';

type PasswordResetDialogProps = {
  subject: string;
  onSubmit: (password: string) => Promise<{ revoked: number }>;
  invalidates: Array<string>;
  onClose: () => void;
};

/** Sets a new password for somebody else. The old one stops working and their sessions end (AC-04.1). */
export const PasswordResetDialog = ({
  subject,
  onSubmit,
  invalidates,
  onClose,
}: PasswordResetDialogProps) => {
  const t = useT();
  const [password, setPassword] = useState<string>(generatePassword);
  const [revoked, setRevoked] = useState<number | null>(null);
  const mutation = useAppMutation(onSubmit, invalidates, (result) => setRevoked(result.revoked));

  return (
    <FormDialog
      title={t('password.resetTitle', { subject })}
      onClose={onClose}
      onSubmit={() => (revoked === null ? mutation.mutate(password) : onClose())}
      submitLabel={revoked === null ? t('password.reset') : t('common.close')}
      isBusy={mutation.isPending}
      error={mutation.error}
    >
      {revoked === null ? (
        <PasswordField value={password} onChange={setPassword} />
      ) : (
        <Alert severity="success" variant="outlined">
          {t('password.resetDone', { count: revoked })}
        </Alert>
      )}
    </FormDialog>
  );
};
